import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, List, ListItem, ListItemText, Link, Alert, CircularProgress, Button, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, Tabs, Tab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import api from '../api';
import CsvUploader from '../components/admin/CsvUploader';
import FeedbackViewer from '../components/admin/FeedbackViewer';
import UserManager from '../components/admin/UserManager';

const AdminDashboard = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [uploadToDelete, setUploadToDelete] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const res = await api.get('/csv/my-uploads');
      setUploads(res.data);
      setError('');
    } catch (err) {
      console.error("Fetch uploads error:", err);
      setError('Failed to fetch uploads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploads();
  }, []);

  const handleUploadSuccess = () => {
    fetchUploads(); // Refresh the list of uploads
  };

  const handleExportUpload = async (upload) => {
    try {
      setExportingId(upload.id);
      const res = await api.get(`/csv/export/${upload.id}`, { responseType: 'blob' });
      let filename = `${upload.file_name.replace(/\.csv$/i, '')}_results.csv`;
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export upload error:", err);
      setError('Failed to export upload results.');
    } finally {
      setExportingId(null);
    }
  };

  const openDeleteConfirm = (upload) => {
    setUploadToDelete(upload);
    setIsConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setUploadToDelete(null);
    setIsConfirmOpen(false);
  };

  const handleDelete = async () => {
    if (!uploadToDelete) return;
    try {
      await api.delete(`/csv/${uploadToDelete.id}`);
      fetchUploads(); // Refresh list after deletion
      closeDeleteConfirm();
    } catch (err) {
      console.error("Delete upload error:", err);
      setError('Failed to delete upload.');
    }
  };

  if (selectedUpload) {
    return <FeedbackViewer upload={selectedUpload} onBack={() => setSelectedUpload(null)} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
          <Tab icon={<CloudUploadIcon />} iconPosition="start" label="CSV Uploads & Feedback" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="User Management" />
        </Tabs>
      </Box>

      {currentTab === 1 ? (
        <UserManager />
      ) : (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>Upload New CSV</Typography>
            <CsvUploader onUploadSuccess={handleUploadSuccess} />
          </Paper>

          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>My Uploads</Typography>
            {loading && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && (
              <List>
                {uploads.length > 0 ? uploads.map((upload) => (
                  <ListItem key={upload.id} divider>
                <ListItemText
                  primary={upload.file_name}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary">
                        Uploaded on: {new Date(upload.created_at).toLocaleDateString()}
                      </Typography>
                      <br />
                      Shareable Link: 
                      <Link href={`/review/${upload.shareable_link}`} target="_blank" rel="noopener">
                        {`/review/${upload.shareable_link}`}
                      </Link>
                    </>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={exportingId === upload.id ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                    onClick={() => handleExportUpload(upload)}
                    disabled={exportingId === upload.id}
                    sx={{ mr: 1 }}
                  >
                    Export
                  </Button>
                  <Button variant="outlined" onClick={() => setSelectedUpload(upload)} sx={{ mr: 1 }}>
                    View Feedback
                  </Button>
                  <IconButton edge="end" aria-label="delete" onClick={() => openDeleteConfirm(upload)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            )) : (
              <Typography>No uploads found.</Typography>
            )}
          </List>
        )}
      </Paper>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={isConfirmOpen}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the file "<strong>{uploadToDelete?.file_name}</strong>"?
            This action is permanent and will delete all associated feedback.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;